import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  adminApi,
  type AdminDailyStats as DailyStats,
  type AdminDailyStatsMetric,
  type AdminDailyStatsMetricKey
} from '../services/api';
import {
  DAILY_PERIODS,
  niceMax,
  parseDay,
  smoothPath,
  summarizePeriod,
  type DailyPeriod
} from '../utils/dailyStats';

const METRICS: AdminDailyStatsMetricKey[] = [
  'pingReserved',
  'paidOut',
  'signed',
  'signedAfterRot',
  'unsignedAmount',
  'unsignedCount'
];

const WIDTH = 1000;
const HEIGHT = 200;
const amountFormat = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 });
const decimalFormat = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 1 });

/**
 * "Ekonomi och kontrakt per dag" on the admin home page (ClickUp 869feyzkk):
 * six daily line charts with their own period and a KPI against the
 * previous period of the same length.
 */
export function AdminDailyStats({ includeTests = false }: { includeTests?: boolean }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DailyStats | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // The longest period once: each widget slices its own days from it.
    setStats(null);
    setFailed(false);
    adminApi.getDailyStats(30, includeTests)
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [includeTests]);

  return (
    <section className="admin-daily-stats mt-4" aria-labelledby="adminDailyStatsTitle">
      <div className="mb-3">
        <h4 id="adminDailyStatsTitle" className="fw-bold mb-1">{t('adminHome.daily.title')}</h4>
        <p className="text-muted small mb-0">{t('adminHome.daily.subtitle')}</p>
      </div>
      {failed ? (
        <div className="alert alert-danger" role="alert">{t('adminHome.daily.loadError')}</div>
      ) : !stats ? (
        <div className="d-flex align-items-center gap-2 text-muted" role="status">
          <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
          {t('common.loading')}
        </div>
      ) : (
        <div className="row g-4 row-cols-1 row-cols-xl-2">
          {METRICS.map((key) => (
            <div key={key} className="col">
              <DailyStatWidget metricKey={key} stats={stats} metric={stats.metrics[key]} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

type WidgetProps = {
  metricKey: AdminDailyStatsMetricKey;
  stats: DailyStats;
  metric: AdminDailyStatsMetric;
};

function DailyStatWidget({ metricKey, stats, metric }: WidgetProps) {
  const { t, i18n } = useTranslation();
  const [days, setDays] = useState<DailyPeriod>(7);
  const [hover, setHover] = useState<number | null>(null);
  const summary = useMemo(() => summarizePeriod(stats, metric, days), [stats, metric, days]);
  const locale = i18n.language === 'en' ? 'en-GB' : 'sv-SE';
  const dayFormat = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  const fullDayFormat = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  const formatDay = (day: string) => dayFormat.format(parseDay(day));
  const isCount = metric.unit === 'st';
  const formatValue = (value: number) =>
    isCount
      ? t('adminHome.daily.count', { value: amountFormat.format(value) })
      : t('adminHome.daily.amount', { value: amountFormat.format(value) });
  const formatAxis = (value: number) => {
    if (isCount) return amountFormat.format(value);
    if (value >= 1000000) return t('adminHome.daily.millions', { value: decimalFormat.format(value / 1000000) });
    if (value >= 1000) return t('adminHome.daily.thousands', { value: amountFormat.format(value / 1000) });
    return amountFormat.format(value);
  };

  const { values, dates } = summary;
  const count = values.length;
  const max = niceMax(Math.max(...values, 0), isCount);
  const x = (index: number) => (count > 1 ? (index / (count - 1)) * WIDTH : WIDTH / 2);
  const y = (value: number) => HEIGHT - (Math.max(value, 0) / max) * HEIGHT;
  const line = smoothPath(values.map((value, index) => [x(index), y(value)]), HEIGHT);
  const area = line ? `${line} L${x(count - 1)},${HEIGHT} L${x(0)},${HEIGHT} Z` : '';
  const ticks = [0, 1, 2, 3, 4].map((step) => ({ top: 100 - step * 25, label: formatAxis((max * step) / 4) }));
  const labelStep = days <= 7 ? 1 : days <= 14 ? 2 : 5;
  // Counted from the last day; every other label is hidden on narrow screens.
  const xLabels: Array<{ index: number; text: string; minor: boolean }> = [];
  for (let index = count - 1, n = 0; index >= 0; index -= labelStep, n += 1) {
    xLabels.unshift({ index, text: formatDay(dates[index]), minor: n % 2 === 1 });
  }
  const position = (index: number) => (count > 1 ? (index / (count - 1)) * 100 : 50);
  const hovered = hover !== null && hover < count ? hover : null;
  const change = summary.changePercent;
  const direction = change === null || Math.abs(change) < 0.05 ? 'flat' : change > 0 ? 'up' : 'down';
  const title = t(`adminHome.daily.metrics.${metricKey}`);
  const gradientId = `adminDailyGradient-${metricKey}`;
  const range = dates.length > 0 ? `${formatDay(dates[0])} – ${fullDayFormat.format(parseDay(dates[dates.length - 1]))}` : '';

  return (
    <section className={`card h-100 mb-0 admin-daily-chart admin-daily-chart--${metricKey}`} data-testid={`daily-stat-${metricKey}`}>
      <div className="card-body d-flex flex-column gap-3">
        <div className="d-flex justify-content-between align-items-start gap-3">
          <div className="min-w-0">
            <h5 className="fs-6 fw-semibold mb-1">{title}</h5>
            <p className="small text-muted mb-0">
              {t(metric.kind === 'balance' ? 'adminHome.daily.rangeBalance' : 'adminHome.daily.rangeFlow', { range })}
            </p>
          </div>
          <label className="visually-hidden" htmlFor={`adminDailyPeriod-${metricKey}`}>
            {t('adminHome.daily.periodLabel', { metric: title })}
          </label>
          <select
            id={`adminDailyPeriod-${metricKey}`}
            className="form-select form-select-sm admin-daily-chart__period"
            value={days}
            onChange={(event) => {
              setDays(Number(event.target.value) as DailyPeriod);
              setHover(null);
            }}
          >
            {DAILY_PERIODS.map((period) => (
              <option key={period} value={period}>{t(`adminHome.daily.periods.${period}`)}</option>
            ))}
          </select>
        </div>

        <div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="admin-daily-chart__total" data-testid="daily-stat-total">{formatValue(summary.total)}</span>
            <span className={`admin-daily-chart__chip admin-daily-chart__chip--${direction}`} data-testid="daily-stat-change">
              {direction === 'up' && <i className="feather-arrow-up-right" aria-hidden="true"></i>}
              {direction === 'down' && <i className="feather-arrow-down-right" aria-hidden="true"></i>}
              {change === null
                ? t('adminHome.daily.noChange')
                : t('adminHome.daily.changePercent', { value: `${change > 0 ? '+' : ''}${decimalFormat.format(change)}` })}
            </span>
          </div>
          <p className="small text-muted mb-0 mt-1">
            {t(metric.kind === 'balance' ? 'adminHome.daily.compareBalance' : `adminHome.daily.compareFlow.${days}`, {
              value: formatValue(summary.previousTotal)
            })}
          </p>
        </div>

        <div className="d-flex gap-2">
          <div className="admin-daily-chart__y-axis" aria-hidden="true">
            {ticks.map((tick) => (
              <span key={tick.top} className="admin-daily-chart__y-label" style={{ top: `${tick.top}%` }}>{tick.label}</span>
            ))}
          </div>
          <div className="flex-grow-1 min-w-0">
            <div className="admin-daily-chart__plot" onMouseLeave={() => setHover(null)}>
              {ticks.map((tick) => (
                <div
                  key={tick.top}
                  className={`admin-daily-chart__grid ${tick.top === 100 ? 'admin-daily-chart__grid--base' : ''}`}
                  style={{ top: `${tick.top}%` }}
                ></div>
              ))}
              <svg
                className="admin-daily-chart__svg"
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                preserveAspectRatio="none"
                role="img"
                aria-label={t('adminHome.daily.ariaLabel', {
                  metric: title,
                  period: t(`adminHome.daily.periods.${days}`),
                  total: formatValue(summary.total)
                })}
              >
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" className="admin-daily-chart__stop-top"></stop>
                    <stop offset="1" className="admin-daily-chart__stop-bottom"></stop>
                  </linearGradient>
                </defs>
                <path d={area} fill={`url(#${gradientId})`}></path>
                <path d={line} className="admin-daily-chart__line"></path>
              </svg>
              {hovered !== null && (
                <>
                  <div className="admin-daily-chart__cursor" style={{ left: `${position(hovered)}%` }}></div>
                  <div
                    className="admin-daily-chart__dot"
                    style={{ left: `${position(hovered)}%`, top: `${(y(values[hovered]) / HEIGHT) * 100}%` }}
                  ></div>
                  <div
                    className={`admin-daily-chart__tooltip ${position(hovered) > 65 ? 'admin-daily-chart__tooltip--left' : ''}`}
                    style={{ left: `${position(hovered)}%` }}
                    data-testid="daily-stat-tooltip"
                  >
                    <div className="admin-daily-chart__tooltip-date">{fullDayFormat.format(parseDay(dates[hovered]))}</div>
                    <div className="admin-daily-chart__tooltip-value">{formatValue(values[hovered])}</div>
                  </div>
                </>
              )}
              {values.map((value, index) => (
                <button
                  key={dates[index]}
                  type="button"
                  className="admin-daily-chart__hit"
                  style={{
                    left: `${count > 1 ? ((index - 0.5) / (count - 1)) * 100 : 0}%`,
                    width: `${count > 1 ? 100 / (count - 1) : 100}%`
                  }}
                  tabIndex={-1}
                  aria-label={`${fullDayFormat.format(parseDay(dates[index]))}: ${formatValue(value)}`}
                  onMouseEnter={() => setHover(index)}
                  onFocus={() => setHover(index)}
                  onClick={() => setHover(index)}
                ></button>
              ))}
            </div>
            <div className="admin-daily-chart__x-axis" aria-hidden="true">
              {xLabels.map((label) => (
                <span
                  key={label.index}
                  className={`admin-daily-chart__x-label ${
                    label.index === count - 1 ? 'admin-daily-chart__x-label--end' : label.index === 0 ? 'admin-daily-chart__x-label--start' : ''
                  } ${label.minor ? 'admin-daily-chart__x-label--minor' : ''}`}
                  style={{ left: `${position(label.index)}%` }}
                >
                  {label.text}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
